import React from 'react'
import cn from 'classnames'
import { useQuery } from '@tanstack/react-query'
import api from 'api/api'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import { BITCOIN_UNIT, SATOSHIS, USD_UNIT } from 'lib/bsv-util'
import styles from './profile-earnings.module.scss'

export type UserEarnings = {
    total_sats: number
    post_sats: number
    music_sats: number
    share_sats: number
    host_sats: number
    ordinal_sats: number
}

type ProfileTab = 'posts' | 'ordinals' | 'music'

// Fixed order, so a source keeps its color whichever others are empty.
const SOURCES: Array<{ key: keyof UserEarnings; label: string; slot: string; tab?: ProfileTab }> = [
    { key: 'post_sats', label: 'Posts', slot: styles.slot1, tab: 'posts' },
    { key: 'ordinal_sats', label: 'Ordinal sales', slot: styles.slot2, tab: 'ordinals' },
    { key: 'share_sats', label: 'Sharing', slot: styles.slot3 },
    { key: 'host_sats', label: 'Hosts', slot: styles.slot4 },
    { key: 'music_sats', label: 'Music', slot: styles.slot5, tab: 'music' },
]

// Earnings read at a glance: whole BSV amounts keep two decimals and smaller
// ones three significant digits, never all eight.
const formatBsv = (sats: number) => {
    const bsv = sats / SATOSHIS
    const digits = bsv >= 1 ? { maximumFractionDigits: 2 } : { maximumSignificantDigits: 3 }
    return `${bsv.toLocaleString('en-US', digits)} BSV`
}

// A profile's lifetime earnings: the total, one bar split by source, and a
// legend with each source's amount. Sources with a profile tab open it.
export default function ProfileEarnings({
    userId,
    onOpenTab,
}: {
    userId?: string
    onOpenTab?: (tab: ProfileTab) => void
}) {
    const { formatSatsAmount: formatSatsDefault, displayUnit } = useBsvDisplay()
    const { data } = useQuery<UserEarnings>({
        queryKey: ['userEarnings', String(userId)],
        enabled: Boolean(userId),
        staleTime: 5 * 60 * 1000,
        // The app's client never refetches on mount by default; a revisit
        // after the server cache turns over should show new earnings.
        refetchOnMount: true,
        queryFn: () => api.getUserEarnings(userId),
    })
    if (!data) return null

    const formatSatsAmount = (sats: number) =>
        displayUnit(sats) === BITCOIN_UNIT ? formatBsv(sats) : formatSatsDefault(sats)

    const total = Number(data.total_sats)
    const sources = SOURCES.map(source => ({ ...source, sats: Number(data[source.key]) })).filter(
        source => source.sats > 0,
    )

    return (
        <div
            className={styles.profileEarnings}
            onClick={ev => ev.stopPropagation()}
            data-testid="profile-earnings"
        >
            <div className={styles.hud}>
                <span>01 Earned</span>
                <span>
                    Lifetime <span className={styles.ticks}>▪▪▪▪</span>
                </span>
            </div>
            <div className={styles.total}>
                <span
                    className={cn(
                        styles.totalAmount,
                        displayUnit(total) === USD_UNIT ? styles.usd : styles.sats,
                    )}
                >
                    {formatSatsAmount(total)}
                </span>
            </div>

            {sources.length > 0 && (
                <>
                    <div className={styles.bar} aria-hidden="true">
                        {sources.map(source => (
                            <span
                                key={source.key}
                                className={cn(styles.segment, source.slot)}
                                style={{ flexGrow: source.sats }}
                                title={`${source.label}: ${formatSatsAmount(source.sats)}`}
                            />
                        ))}
                    </div>
                    <div className={styles.legend}>
                        {sources.map((source, index) => {
                            const content = (
                                <>
                                    <span className={cn(styles.swatch, source.slot)} />
                                    <span className={styles.legendIndex}>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                    <span className={styles.legendLabel}>{source.label}</span>
                                    <span className={styles.legendAmount}>
                                        {formatSatsAmount(source.sats)}
                                    </span>
                                </>
                            )
                            const testId = `profile-earnings-${source.key.replace('_sats', '')}`
                            return source.tab && onOpenTab ? (
                                <button
                                    key={source.key}
                                    type="button"
                                    className={cn(styles.legendItem, styles.legendButton)}
                                    onClick={() => onOpenTab(source.tab)}
                                    data-testid={testId}
                                >
                                    {content}
                                </button>
                            ) : (
                                <span
                                    key={source.key}
                                    className={styles.legendItem}
                                    data-testid={testId}
                                >
                                    {content}
                                </span>
                            )
                        })}
                    </div>
                </>
            )}
        </div>
    )
}
